/** @fileoverview Resolves row and globals refs and selects component record keys. */

import type {
  FabrikDataSourceRefValue,
  FabrikGlobals,
  FabrikGlobalsRefValue,
  FabrikInputRefValue,
  FabrikRecordKeyRefValue,
  InputSnapshot,
  FabrikRecordRefValue,
  FabrikRowField,
  FabrikRowValues,
} from '../../types/apps';

/** A ref that reads one value from row data or from the globals. */
export type FabrikRowOrGlobalsRefValue =
  | FabrikRecordRefValue
  | FabrikDataSourceRefValue
  | FabrikGlobalsRefValue;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Reads one cell from a composed list row or a screen's row values.
 * A field named `userNav/firstName` walks into the expanded `userNav` object inside that datasource's row.
 * A value that is still an object or a list is not a cell.
 * @param rowValues the row values, keyed by datasource id
 * @param rowField the datasource id and the field, with `/` between the steps of a path
 */
export const valueForColumn = (rowValues: FabrikRowValues, rowField: FabrikRowField): unknown => {
  const { dataSourceId, field } = rowField;
  const sourceRow = rowValues[dataSourceId];
  if (!isPlainObject(sourceRow)) {
    return undefined;
  }
  // A static item may store the whole name as one key, so the literal key wins over the path.
  const value = Object.prototype.hasOwnProperty.call(sourceRow, field)
    ? sourceRow[field]
    : field
        .split('/')
        .reduce<unknown>(
          (current, step) =>
            isPlainObject(current) && Object.prototype.hasOwnProperty.call(current, step)
              ? current[step]
              : undefined,
          sourceRow
        );
  return typeof value === 'object' && value !== null ? undefined : value;
};

/**
 * Reads one globals field, or undefined when the field is not a known globals field.
 * @param field the globals field name
 * @param globals the globals values of this render
 */
export const valueForGlobal = (field: string, globals: FabrikGlobals): unknown =>
  Object.prototype.hasOwnProperty.call(globals, field)
    ? globals[field as keyof FabrikGlobals]
    : undefined;

/**
 * Resolves a record, dataSource, or globals ref. Record and dataSource refs read the row values.
 * @param rowValues the row values, keyed by datasource id
 * @param ref the ref to read
 * @param globals the globals values of this render
 */
export const valueForReference = (
  rowValues: FabrikRowValues,
  ref: FabrikRowOrGlobalsRefValue | FabrikInputRefValue,
  globals: FabrikGlobals,
  inputs: InputSnapshot = {}
): unknown => {
  if (ref.scope === 'globals') return valueForGlobal(ref.field, globals);
  if (ref.scope === 'inputs')
    return ref.formId ? inputs.forms?.[ref.formId]?.[ref.field] : inputs.standalone?.[ref.field];
  return valueForColumn(rowValues, { dataSourceId: ref.dataSourceId, field: ref.field });
};

/**
 * Selects the record-key values of the row values. Globals refs are skipped here: the
 * component-state calls add their values under `GLOBALS` when they build the request.
 * @param rowValues the screen's row values, keyed by datasource id
 * @param recordKey the record and globals refs that identify the record
 */
export const fieldsForRecordKey = (
  rowValues: FabrikRowValues,
  recordKey: FabrikRecordKeyRefValue[]
): FabrikRowValues => {
  const selected: FabrikRowValues = {};
  for (const ref of recordKey) {
    if (ref.scope !== 'record') continue;
    const value = valueForColumn(rowValues, ref);
    if (value === undefined) continue;
    const { dataSourceId, field } = ref;
    const selectedRow = Object.prototype.hasOwnProperty.call(selected, dataSourceId)
      ? selected[dataSourceId]
      : undefined;
    selected[dataSourceId] = { ...(selectedRow ?? {}), [field]: value };
  }
  return selected;
};
